/* LAMAR V4 — exact product groups through filtered collections.
   No PHP, token or catalogue scan. No native fallback for a populated group.
   Two source requests at a time, after window.load. Session cache: 5 minutes.
   One card per product, preserving the existing product-card rendering. */
(() => {
  const TAG = 'lamar-group-recommendations';
  if (customElements.get(TAG)) return;
  const VERSION = '4';
  const PREFIX = 'lamar-related-v4:';
  const TTL = 5 * 60 * 1000;
  const memory = new Map();
  const parse = html => new DOMParser().parseFromString(html, 'text/html');

  class RelatedGroups extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.group = String(this.dataset.group || '').trim();
      this.limit = Math.min(16, Math.max(4, Number(this.dataset.limit) || 16));
      this.track = this.querySelector('[data-related-track]');
      this.prev = this.querySelector('[data-related-prev]');
      this.next = this.querySelector('[data-related-next]');
      if (!this.track || !this.prev || !this.next) return;
      try {
        this.sources = [...new Set(JSON.parse(this.querySelector('[data-related-sources]').textContent))];
      } catch { this.sources = []; }
      this.resize = () => {
        const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
        this.prev.hidden = this.next.hidden = !overflow;
      };
      this.click = event => {
        const button = event.target.closest('[data-related-prev], [data-related-next]');
        if (!button) return;
        this.track.scrollBy({left: (button === this.prev ? -1 : 1) * this.track.clientWidth, behavior: 'smooth'});
      };
      this.addEventListener('click', this.click);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.resize);
        this.observer.observe(this.track);
      } else window.addEventListener('resize', this.resize);
      this.loaded = () => { this.timer = setTimeout(() => this.load(), 120); };
      if (document.readyState === 'complete') this.loaded();
      else window.addEventListener('load', this.loaded, {once: true});
    }

    disconnectedCallback() {
      window.removeEventListener('load', this.loaded);
      window.removeEventListener('resize', this.resize);
      this.removeEventListener('click', this.click);
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.controller?.abort();
      this.started = false;
    }

    cached(key) {
      try {
        const value = memory.get(key) || JSON.parse(sessionStorage.getItem(key) || 'null');
        if (value && Date.now() - value.time < TTL && Array.isArray(value.html)) return value.html;
      } catch { /* Storage may be disabled. */ }
      return null;
    }

    remember(key, html) {
      const value = {time: Date.now(), html};
      memory.set(key, value);
      if (memory.size > 8) memory.delete(memory.keys().next().value);
      try {
        const keys = Object.keys(sessionStorage).filter(k => k.startsWith(PREFIX));
        keys.sort((a, b) => {
          try { return JSON.parse(sessionStorage.getItem(a)).time - JSON.parse(sessionStorage.getItem(b)).time; }
          catch { return 0; }
        });
        while (keys.length >= 8) sessionStorage.removeItem(keys.shift());
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch { /* Cache is optional. */ }
    }

    async request(url, signal) {
      for (let attempt = 0; attempt < 2; attempt++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const local = new AbortController();
        const abort = () => local.abort();
        signal.addEventListener('abort', abort, {once: true});
        const timeout = setTimeout(abort, 20000);
        let retryable = true;
        try {
          const response = await fetch(url, {signal: local.signal, credentials: 'same-origin'});
          if (!response.ok) {
            retryable = response.status === 429 || response.status >= 500;
            throw new Error('HTTP ' + response.status);
          }
          return await response.text();
        } catch (error) {
          if (signal.aborted || !retryable || attempt === 1) throw error;
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', abort);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    async sourceCards(source, signal) {
      const first = new URL(source, location.origin);
      if (first.origin !== location.origin || !first.pathname.includes('/collections/')) throw new Error('Invalid collection URL');
      first.search = '';
      first.searchParams.set('filter.p.m.custom.related_group', this.group);
      first.searchParams.set('section_id', 'lamar-related-group-results');
      const html = [], visited = new Set();
      let url = first;
      // The group is usually <=100 products; normally one page per collection.
      // Follow all filtered pages rather than silently truncating recommendations.
      while (url) {
        if (visited.has(url.href)) throw new Error('Repeated collection page');
        visited.add(url.href);
        const doc = parse(await this.request(url.href, signal));
        const root = doc.querySelector('[data-related-group-results]');
        if (!root || root.dataset.relatedVersion !== VERSION) throw new Error('Related results section missing or outdated');
        if (root.dataset.status === 'empty') break;
        if (root.dataset.status !== 'ok' || root.dataset.group !== this.group) {
          throw new Error(first.pathname + ': ' + (root.dataset.status || 'group mismatch'));
        }
        for (const card of root.querySelectorAll('template[data-related-card]')) {
          if (card.dataset.group === this.group) html.push(card.outerHTML);
        }
        if (!root.dataset.next) break;
        const next = new URL(root.dataset.next, location.origin);
        if (next.origin !== first.origin || next.pathname !== first.pathname) throw new Error('Unexpected pagination');
        next.searchParams.set('filter.p.m.custom.related_group', this.group);
        next.searchParams.set('section_id', 'lamar-related-group-results');
        url = next;
      }
      return html;
    }

    async groupCards(signal) {
      if (!this.sources.length) throw new Error('No source collections configured');
      // Includes current product in cache: the same group can be reused on another product page.
      const key = PREFIX + JSON.stringify([this.group, this.sources, document.documentElement.lang, window.Shopify?.country, window.Shopify?.currency?.active]);
      const cached = this.cached(key);
      if (cached) return {html: cached, partial: false};
      const results = new Array(this.sources.length);
      let cursor = 0;
      const worker = async () => {
        while (cursor < this.sources.length) {
          const index = cursor++;
          try { results[index] = {html: await this.sourceCards(this.sources[index], signal)}; }
          catch (error) {
            if (signal.aborted) throw error;
            results[index] = {error};
            console.warn('[LAMAR related]', error.message);
          }
        }
      };
      await Promise.all([worker(), worker()]);
      const partial = results.some(result => result.error);
      const html = results.flatMap(result => result.html || []);
      if (!partial) this.remember(key, html);
      return {html, partial};
    }

    pick(html, random) {
      const products = new Map();
      const doc = parse(html.join(''));
      for (const card of doc.querySelectorAll('template[data-related-card]')) {
        const id = card.dataset.productId;
        if (!id || id === this.dataset.productId || products.has(id)) continue;
        if (this.group && card.dataset.group !== this.group) continue;
        products.set(id, card);
      }
      const cards = [...products.values()];
      if (random) {
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
      }
      return cards.slice(0, this.limit);
    }

    async load() {
      const controller = new AbortController();
      this.controller?.abort();
      this.controller = controller;
      this.dataset.relatedState = 'loading';
      try {
        let html, partial = false;
        if (this.group) {
          ({html, partial} = await this.groupCards(controller.signal));
          this.dataset.relatedSource = partial ? 'group-partial' : 'group';
        } else {
          const url = new URL(this.dataset.nativeUrl, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid native URL');
          const doc = parse(await this.request(url.href, controller.signal));
          const root = doc.querySelector('[data-related-native]');
          if (!root) throw new Error('Native recommendations unavailable');
          html = [...root.querySelectorAll('template[data-related-card]')].map(card => card.outerHTML);
          this.dataset.relatedSource = 'native-no-group';
        }
        if (controller.signal.aborted || !this.isConnected) return;
        const cards = this.pick(html, Boolean(this.group));
        this.track.replaceChildren(...cards.map(card => card.content.cloneNode(true)));
        this.track.scrollLeft = 0;
        this.hidden = cards.length === 0;
        this.dataset.relatedState = cards.length ? (partial ? 'partial' : 'ready') : (partial ? 'unavailable' : 'empty');
        requestAnimationFrame(this.resize);
      } catch (error) {
        if (controller.signal.aborted) return;
        this.hidden = true;
        this.dataset.relatedState = 'unavailable';
        console.warn('[LAMAR related]', error.message);
      }
    }
  }
  customElements.define(TAG, RelatedGroups);
})();